import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLeads } from '../../../context/LeadContext';
import { useAuth } from '../../../context/AuthContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../../api/config';
import { get, post, del, put } from '../../../api/apiClient';
import {
  ArrowLeft,
  Save,
  X,
  User,
  Building,
  MapPin,
  Tag,
  FileText,
  AlertCircle,
  ChevronDown,
  Search,
  Upload,
  Trash2,
  Loader2,
  Link as LinkIcon,
  UserPlus
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { toast } from 'react-hot-toast';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../../components/common/LocationSelect/LocationSelect';
import {
  validateNumberInput,
  validatePhoneInput,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import { toTitleCase } from '../../../utils/stringUtils';

const ManualLeadEntry = ({ onBack, leadId: propLeadId, onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editLeadId = propLeadId || searchParams.get('edit');
  const isEditMode = !!editLeadId;

  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { getLeadById, loading } = useLeads();

  const isSalesExecutive = user?.role === 'sales_executive';
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    contactMethod: 'email',
    whatsappPhone: '',
    alternatePhone: '',
    designation: '',
    linkedinProfileUrl: '',

    // Company Information
    company: '',
    website: '',
    industry: '',
    companysize: '',
    annualRevenue: '',
    gstNumber: '',

    companyDocuments: [],

    // Address Information
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    region: '',

    // Lead Information
    leadGenerationDate: new Date().toISOString().split('T')[0],
    source: 'manual_entry',
    status: 'new',
    leadValue: '',
    probability: '',
    budget: '',
    revenueMonthly: '',
    revenueOneTime: '',
    revenueType: '',
    costType: '',
    costOneTime: '',
    costMrr: '',
    costDurationMonths: '',
    costQuarterly: '',
    totalCost: '',
    timeline: '',
    productRequirement: '',
    decisionMaker: '',
    category: '',
    businessUnit: '',
    leadBucket: '',
    owner: '',
    assignedTo: '', // Store user ID for backend
    originalGenerator: '', // Store user ID for backend
    originalGeneratorName: '',
    expectedCloseDate: '',
    needManagementIntervention: '',
    stagingInstance: '',

    // Additional Information
    description: '',
    notes: '',
    tags: [],
    lossReason: '',
    scoreLabel: 'cold',
    contactDetailsRaw: '',
    nextAction: '',
    lastActiveDate: '',
    lastActiveChannel: '',
    lastFollowupDate: '',
    nextFollowupDate: '',
    followupCadence: '',
    followupCount: '0'
  });

  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('personal');
  const [originalStatus, setOriginalStatus] = useState(null); // Track original status
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const [generatorSearchTerm, setGeneratorSearchTerm] = useState('');
  const [isGeneratorDropdownOpen, setIsGeneratorDropdownOpen] = useState(false);
  const generatorDropdownRef = useRef(null);
  const hasLoadedRef = useRef(false); // Track if lead data has been loaded

  const [isDragging, setIsDragging] = useState(null);
  const prevEditIdRef = useRef(null);

  // Reset hasLoaded when edit ID changes
  useEffect(() => {
    if (editLeadId !== prevEditIdRef.current) {
      hasLoadedRef.current = false;
      prevEditIdRef.current = editLeadId;
    }
  }, [editLeadId]);

  // Auto-assign to self if sales executive and creating a new lead
  useEffect(() => {
    if (!isEditMode && isSalesExecutive && user && !formData.assignedTo) {
      const userId = user.id || user._id || user.user;
      if (userId) {
        setFormData(prev => ({ ...prev, assignedTo: userId }));
      }
    }
  }, [isEditMode, isSalesExecutive, user, formData.assignedTo]);

  // Geographic state for LocationSelect
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState({
    country: null,
    state: null,
    city: null
  });

  // React Query for Master Data
  const { data: masterData, isLoading: isLoadingMasters } = useQuery({
    queryKey: ['leadMasterData'],
    queryFn: async () => {
      const [indResult, dmResult, srcResult, docTypeResult, categoryResult, statusResult, revenueResult, bucketResult, buResult, channelResult, companySizeResult] = await Promise.all([
        get(`${API_ENDPOINTS.CRM.CONFIG.INDUSTRIES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.DECISION_MAKERS}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_SOURCES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_DOCUMENT_TYPES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_CATEGORIES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_STATUSES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.REVENUE_TYPES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_BUCKETS}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.BUSINESS_UNITS}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LAST_ACTIVE_CHANNELS}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.COMPANY_SIZES}?active=true`)
      ]);
      return {
        industries: indResult.data || [],
        decisionMakers: dmResult.data || [],
        sources: srcResult.data || [],
        documentTypes: docTypeResult.data || [],
        leadCategories: categoryResult.data || [],
        leadStatuses: statusResult.data || [],
        revenueTypes: revenueResult.data || [],
        leadBuckets: bucketResult.data || [],
        businessUnits: buResult.data || [],
        lastActiveChannels: channelResult.data || [],
        companySizes: companySizeResult.data || []
      };
    }
  });

  const masterIndustries = masterData?.industries || [];
  const masterDecisionMakers = masterData?.decisionMakers || [];
  const masterSources = masterData?.sources || [];
  const masterLeadDocumentTypes = masterData?.documentTypes || [];
  const masterLeadCategories = masterData?.leadCategories || [];
  const masterLeadStatuses = masterData?.leadStatuses || [];
  const masterRevenueTypes = masterData?.revenueTypes || [];
  const masterLeadBuckets = masterData?.leadBuckets || [];
  const masterBusinessUnits = masterData?.businessUnits || [];
  const masterLastActiveChannels = masterData?.lastActiveChannels || [];
  const masterCompanySizes = masterData?.companySizes || [];

  const parseCurrencyValue = (value) => {
    const parsed = parseFloat(String(value || '').replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const calculatedCostQuarterly = formData.costType === 'recurring'
    ? parseCurrencyValue(formData.costDurationMonths) * parseCurrencyValue(formData.costMrr)
    : 0;
  const calculatedTotalCost = formData.costType === 'fixed'
    ? parseCurrencyValue(formData.totalCost)
    : parseCurrencyValue(formData.costOneTime) + calculatedCostQuarterly;

  const assigneeSearchQuery = isUserDropdownOpen ? userSearchTerm : '';
  const generatorSearchQuery = isGeneratorDropdownOpen ? generatorSearchTerm : '';

  // React Query for lead assignees / original generator (super_admin, sales_manager, sales_executive)
  const { data: userData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['crmLeadAssignees', assigneeSearchQuery, generatorSearchQuery],
    queryFn: async () => {
      const params = new URLSearchParams();
      const search = assigneeSearchQuery || generatorSearchQuery;
      if (search) params.append('search', search.trim());
      params.append('limit', 'all');
      params.append('roleFilter', 'lead_assignment');

      const response = await get(`${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?${params.toString()}`);
      if (response.responseCode === 200) {
        return (response.data?.users || []).map(user => ({
          id: user.user || user._id || user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          role: user.role || '',
          department: user.department || '',
          designation: user.designation || ''
        })).sort((a, b) => a.name.localeCompare(b.name));
      }
      return [];
    },
    enabled: isUserDropdownOpen || isGeneratorDropdownOpen || hasLoadedRef.current
  });

  const filteredUsers = useMemo(() => (userData || []).slice(0, 10), [userData]);
  const filteredGenerators = useMemo(() => (userData || []).slice(0, 10), [userData]);

  // React Query for Lead Data (Edit Mode)
  const { data: fetchedLead, isLoading: isLoadingLead } = useQuery({
    queryKey: ['lead', editLeadId],
    queryFn: async () => {
      const result = await getLeadById(editLeadId);
      return result;
    },
    enabled: isEditMode && !!editLeadId
  });

  // Sync fetched lead data to formData state
  useEffect(() => {
    if (fetchedLead && !hasLoadedRef.current) {
      const result = fetchedLead;
      setFormData({
        firstName: result.firstName || '',
        lastName: result.lastName || '',
        email: result.email || '',
        phone: result.phone || '',
        alternatePhone: result.alternatePhone || '',
        company: result.company || '',
        website: result.website || '',
        industry: result.industryId || result.industry?._id || result.industry || '',
        companysize: result.companysize?._id || result.companysize || '',
        annualRevenue: result.annualRevenue ? String(result.annualRevenue) : '',
        companyDocuments: Array.isArray(result.companyDocuments) && result.companyDocuments.length > 0
          ? result.companyDocuments.map(doc => {
            const pdfData = doc.companyPdf;
            let normalizedPdf = pdfData;

            // Normalize string URLs to object format for consistent handling
            if (typeof pdfData === 'string' && pdfData && !pdfData.startsWith('{')) {
              const parts = pdfData.split('/');
              const filename = parts[parts.length - 1] || 'Document.pdf';
              normalizedPdf = {
                url: pdfData,
                name: filename,
                originalName: filename,
                public_id: doc.companyPdfPublicId || ''
              };
            }

            return {
              documentTypeId: doc.documentTypeId || (typeof doc.documentType === 'object' ? doc.documentType?._id : (typeof doc.documentType === 'string' ? doc.documentType : '')),
              documentTypeName: doc.documentTypeName || (typeof doc.documentType === 'object' ? doc.documentType?.name : (doc.documentType || '')),
              documentMethod: doc.documentMethod || (doc.documentLink ? 'link' : 'upload'),
              documentLink: doc.documentLink || '',
              companyPdf: normalizedPdf || '',
              companyPdfPublicId: doc.companyPdfPublicId || (normalizedPdf?.public_id || ''),
              certificateNumber: doc.certificateNumber || ''
            };
          })
          : (result.companyPdf ? (() => {
            const pdfData = result.companyPdf;
            let normalizedPdf = pdfData;

            if (typeof pdfData === 'string' && pdfData && !pdfData.startsWith('{')) {
              const parts = pdfData.split('/');
              const filename = parts[parts.length - 1] || 'Legacy Attachment.pdf';
              normalizedPdf = {
                url: pdfData,
                name: filename,
                originalName: filename,
                public_id: result.companyPdfPublicId || ''
              };
            }

            return [{
              documentTypeId: '',
              documentTypeName: 'Legacy Attachment',
              documentMethod: 'upload',
              documentLink: '',
              companyPdf: normalizedPdf,
              companyPdfPublicId: result.companyPdfPublicId || (normalizedPdf?.public_id || ''),
              certificateNumber: ''
            }];
          })() : []),
        address: (typeof result.address === 'string' ? result.address : result.address?.street) || '',
        city: result.city || (result.address?.city) || '',
        state: result.state || (result.address?.state) || '',
        pincode: result.pincode || (result.address?.pincode) || '',
        country: result.country || (result.address?.country) || '',
        source: result.sourceId || result.source?._id || result.source || 'manual_entry',
        status: (() => {
          const stage = result.stage || 'new';
          if (stage === 'disqualified' || result.isQualified === false) return 'disqualified';
          const statusMap = {};
          masterLeadStatuses.forEach(s => {
            statusMap[s.name] = s.value;
            statusMap[s.value] = s.value;
          });
          return statusMap[stage] || stage;
        })(),
        region: mapRegionToDropdown(result.region || result.address?.state || ''),
        leadValue: (result.leadValue !== null && result.leadValue !== undefined) ? String(result.leadValue) : '',
        budget: (result.budget !== null && result.budget !== undefined) ? String(result.budget) : '',
        timeline: mapTimelineToDropdown(result.timeline || ''),
        productRequirement: result.productRequirement || '',
        decisionMaker: result.decisionMakerId || result.decisionMaker?._id || result.decisionMaker || '',
        category: result.category?._id || result.category || '',
        owner: result.owner || '',
        assignedTo: result.assignedToId || result.assignedTo?._id || (typeof result.assignedTo === 'string' ? result.assignedTo : '') || '',
        originalGenerator: result.originalGeneratorId || result.originalGenerator?._id || (typeof result.originalGenerator === 'object' ? result.originalGenerator?._id : '') || '',
        originalGeneratorName: (typeof result.originalGenerator === 'string' ? result.originalGenerator : '') || '',
        description: result.notes || result.description || '',
        notes: result.internalNotes || result.notes || '',
        tags: Array.isArray(result.tags) ? result.tags : (result.tags ? [result.tags] : []),
        lossReason: (() => {
          if (result.lostReason && Array.isArray(result.lostReason) && result.lostReason.length > 0) {
            const sortedReasons = [...result.lostReason].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
            const latest = sortedReasons[0];
            if (typeof latest === 'object' && latest !== null) return latest.reason || '';
            return latest || '';
          }
          return '';
        })(),
        scoreLabel: result.scoreLabel || 'cold',
        // Sync new client-specific fields
        whatsappPhone: result.whatsappPhone || '',
        designation: result.designation || '',
        linkedinProfileUrl: result.linkedinProfileUrl || '',
        revenueMonthly: (result.revenueMonthly !== null && result.revenueMonthly !== undefined) ? String(result.revenueMonthly) : '',
        revenueOneTime: (result.revenueOneTime !== null && result.revenueOneTime !== undefined) ? String(result.revenueOneTime) : '',
        revenueType: result.revenueType || '',
        costType: result.costType === 'one_time' ? 'fixed' : (result.costType || ''),
        costOneTime: (result.costOneTime !== null && result.costOneTime !== undefined) ? String(result.costOneTime) : '',
        costMrr: (result.costMrr !== null && result.costMrr !== undefined) ? String(result.costMrr) : '',
        costDurationMonths: (result.costDurationMonths !== null && result.costDurationMonths !== undefined) ? String(result.costDurationMonths) : '',
        costQuarterly: (result.costQuarterly !== null && result.costQuarterly !== undefined) ? String(result.costQuarterly) : '',
        totalCost: (result.totalCost !== null && result.totalCost !== undefined) ? String(result.totalCost) : '',
        gstNumber: result.gstNumber || '',
        leadGenerationDate: result.leadGenerationDate ? new Date(result.leadGenerationDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        businessUnit: result.businessUnit || '',
        leadBucket: result.leadBucket || '',
        probability: (result.probability !== null && result.probability !== undefined) ? String(result.probability) : '',
        expectedCloseDate: result.expectedCloseDate ? new Date(result.expectedCloseDate).toISOString().split('T')[0] : '',
        needManagementIntervention: result.needManagementIntervention || '',
        stagingInstance: result.stagingInstance || '',
        contactDetailsRaw: result.contactDetailsRaw || '',
        nextAction: result.nextAction || '',
        lastActiveDate: result.lastActiveDate ? new Date(result.lastActiveDate).toISOString().split('T')[0] : '',
        lastActiveChannel: result.lastActiveChannel || '',
        lastFollowupDate: result.lastFollowupDate ? new Date(result.lastFollowupDate).toISOString().split('T')[0] : '',
        nextFollowupDate: result.nextFollowupDate ? new Date(result.nextFollowupDate).toISOString().split('T')[0] : '',
        followupCadence: result.followupCadence || '',
        followupCount: (result.followupCount !== null && result.followupCount !== undefined) ? String(result.followupCount) : '0',
        contactMethod: (result.email && result.phone) ? 'both' : (result.phone ? 'phone' : 'email')
      });

      if (result.industry && typeof result.industry === 'object') setLeadIndustryInfo(result.industry);
      if (result.decisionMaker && typeof result.decisionMaker === 'object') setLeadDecisionMakerInfo(result.decisionMaker);
      if (result.source && typeof result.source === 'object') setLeadSourceInfo(result.source);
      if (result.companysize && typeof result.companysize === 'object') setLeadCompanySizeInfo(result.companysize);
      setOriginalStatus(result.stage || 'new');

      let locationToHydrate = (result.locations && result.locations.length > 0) ? result.locations[0] : null;
      if (locationToHydrate) {
        setSelectedLocation({
          country: locationToHydrate.country || null,
          state: locationToHydrate.address?.state || null,
          city: locationToHydrate.address?.city || null
        });
      } else if (result.country || result.address?.country) {
        const countryVal = result.country || result.address?.country;
        const foundCountry = Country.getAllCountries().find(c => c.name === countryVal || c.isoCode === countryVal);
        if (foundCountry) {
          const countryObj = { id: foundCountry.isoCode, name: foundCountry.name, isoCode: foundCountry.isoCode };
          setSelectedLocation(prev => ({ ...prev, country: countryObj }));
          const stateVal = result.state || result.address?.state;
          if (stateVal) {
            const foundState = State.getStatesOfCountry(foundCountry.isoCode).find(s => s.name === stateVal || s.isoCode === stateVal);
            if (foundState) {
              const stateObj = { id: foundState.isoCode, name: foundState.name, isoCode: foundState.isoCode, countryCode: foundState.countryCode };
              setSelectedLocation(prev => ({ ...prev, state: stateObj }));
              const cityVal = result.city || result.address?.city;
              if (cityVal) {
                const foundCity = City.getCitiesOfState(foundCountry.isoCode, foundState.isoCode).find(c => c.name === cityVal);
                if (foundCity) {
                  setSelectedLocation(prev => ({ ...prev, city: { id: foundCity.name, name: foundCity.name } }));
                }
              }
            }
          }
        }
      }
      hasLoadedRef.current = true;
    }
  }, [fetchedLead, editLeadId]);

  const [leadIndustryInfo, setLeadIndustryInfo] = useState(null);
  const [leadDecisionMakerInfo, setLeadDecisionMakerInfo] = useState(null);
  const [leadSourceInfo, setLeadSourceInfo] = useState(null);
  const [leadCompanySizeInfo, setLeadCompanySizeInfo] = useState(null);

  // Map legacy employee count to companysize reference once master company sizes are loaded dynamically
  useEffect(() => {
    if (isEditMode && fetchedLead && masterCompanySizes?.length > 0 && !formData.companysize) {
      const result = fetchedLead.data || fetchedLead;
      if (!result.companysize && result.employeeCount) {
        const employeeCount = result.employeeCount;
        let foundSize = null;

        // 1. Try finding exact range matching min/max from database option names
        for (const size of masterCompanySizes) {
          if (!size.name) continue;
          const matches = size.name.match(/\d+/g);
          if (!matches || matches.length === 0) continue;
          const min = parseInt(matches[0], 10);
          if (matches.length >= 2) {
            const max = parseInt(matches[1], 10);
            if (employeeCount >= min && employeeCount <= max) {
              foundSize = size;
              break;
            }
          } else {
            if (size.name.includes('+') || size.name.toLowerCase().includes('above') || size.name.toLowerCase().includes('more')) {
              if (employeeCount >= min) {
                foundSize = size;
                break;
              }
            } else if (employeeCount === min) {
              foundSize = size;
              break;
            }
          }
        }

        // 2. If no exact range, find by closest parsed midpoint
        if (!foundSize) {
          let minDiff = Infinity;
          for (const size of masterCompanySizes) {
            if (!size.name) continue;
            const matches = size.name.match(/\d+/g);
            if (!matches || matches.length === 0) continue;
            let repValue;
            if (matches.length >= 2) {
              repValue = (parseInt(matches[0], 10) + parseInt(matches[1], 10)) / 2;
            } else {
              repValue = parseInt(matches[0], 10);
              if (size.name.includes('+')) repValue *= 2;
            }
            const diff = Math.abs(employeeCount - repValue);
            if (diff < minDiff) {
              minDiff = diff;
              foundSize = size;
            }
          }
        }

        if (foundSize) {
          setFormData(prev => ({ ...prev, companysize: foundSize._id }));
        }
      }
    }
  }, [fetchedLead, masterCompanySizes, isEditMode, formData.companysize]);


  // Mutations
  const uploadPdfMutation = useMutation({
    mutationFn: async ({ file, docIndex }) => {
      const pdfFormData = new FormData();
      pdfFormData.append('file', file);
      pdfFormData.append('folder', 'company-documents');
      const result = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, pdfFormData);
      if (result.responseCode !== 200) throw new Error(result.responseMessage || 'Upload failed');
      return { result, docIndex, file };
    },
    onSuccess: ({ result, docIndex, file }) => {
      const pdfMetadata = {
        name: file.name,
        originalName: file.name,
        size: file.size,
        mimetype: file.type,
        url: result.data.url,
        public_id: result.data.public_id
      };

      if (docIndex !== null) {
        setFormData(prev => {
          const newDocs = [...(prev.companyDocuments || [])];
          if (newDocs[docIndex]) {
            newDocs[docIndex] = {
              ...newDocs[docIndex],
              companyPdf: pdfMetadata,
              companyPdfPublicId: result.data.public_id
            };
          }
          return { ...prev, companyDocuments: newDocs };
        });
      } else {
        setFormData(prev => ({
          ...prev,
          companyPdf: pdfMetadata,
          companyPdfPublicId: result.data.public_id
        }));
      }
      toast.success('PDF uploaded successfully');
    },
    onError: (error) => {

      toast.error(error.message || 'An error occurred during PDF upload.');
    }
  });

  const deletePdfMutation = useMutation({
    mutationFn: async ({ publicId, docIndex }) => {
      if (!publicId) return { docIndex };
      const result = await del(`${API_ENDPOINTS.UPLOAD.CLOUDINARY}?public_id=${publicId}&resource_type=raw`);
      if (result.responseCode !== 200) throw new Error(result.responseMessage || 'Delete failed');
      return { docIndex };
    },
    onSuccess: ({ docIndex }) => {
      setFormData(prev => {
        const newDocs = [...(prev.companyDocuments || [])];
        if (newDocs[docIndex]) {
          newDocs[docIndex] = { ...newDocs[docIndex], companyPdf: '', companyPdfPublicId: '' };
        }
        return { ...prev, companyDocuments: newDocs };
      });
      toast.success('Document removed successfully');
    },
    onError: (error) => {

      toast.error(error.message || 'An error occurred while removing the document.');
    }
  });

  const leadMutation = useMutation({
    mutationFn: async (payload) => {
      if (isEditMode && editLeadId) {
        return await put(`${API_ENDPOINTS.CRM.LEADS}/${editLeadId}`, payload);
      } else {
        return await post(API_ENDPOINTS.CRM.LEADS, payload);
      }
    },
    onSuccess: (result) => {
      if (result.responseCode === 200 || result.responseCode === 201) {
        toast.success(`Lead ${isEditMode ? 'updated' : 'created'} successfully!`);
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        if (isEditMode && editLeadId) {
          queryClient.invalidateQueries({ queryKey: ['lead', editLeadId] });
        }
        const backSearchParams = location.state?.from || '';
        navigate(`/lead_list_filters${backSearchParams}`);
      } else {
        throw new Error(result.responseMessage || `Unable to ${isEditMode ? 'update' : 'create'} lead.`);
      }
    },
    onError: (error) => {

      toast.error(error.message || 'An error occurred.');
    }
  });

  const handlePdfUpload = (e, docIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') return toast.error('Only PDF files are allowed.');
    if (file.size > 25 * 1024 * 1024) return toast.error('File size must be less than 25MB.');
    uploadPdfMutation.mutate({ file, docIndex });
  };

  const getDocumentDownloadFilename = (doc) => {
    if (!doc || !doc.companyPdf) return "document.pdf";

    // If companyPdf is the metadata object
    if (typeof doc.companyPdf === 'object' && doc.companyPdf !== null) {
      const fallbackName = doc.companyPdf.originalName || doc.companyPdf.name || "document";
      if (/\.[a-zA-Z0-9]+$/.test(fallbackName)) return fallbackName;

      const mimeType = (doc.companyPdf.mimetype || doc.companyPdf.mimeType || "").toLowerCase();
      if (mimeType === "application/pdf") return `${fallbackName}.pdf`;
      return `${fallbackName}.pdf`;
    }

    // Fallback for string URLs
    const pdfUrl = String(doc.companyPdf || '');
    const parts = pdfUrl.split('/');
    const filename = parts[parts.length - 1] || "document.pdf";
    if (/\.[a-zA-Z0-9]+$/.test(filename)) return filename;
    return `${filename.pdf ? filename : filename + '.pdf'}`;
  };

  const extractObjectKeyFromUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    try {
      const parsed = new URL(url);
      const host = String(parsed.hostname || '').toLowerCase();
      const pathname = decodeURIComponent(parsed.pathname || '').replace(/^\/+/, '');

      if (!pathname) return '';

      if (/\.s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(host) || host.endsWith('.s3.amazonaws.com')) {
        return pathname;
      }

      if (host.startsWith('s3.') || host === 's3.amazonaws.com') {
        const segments = pathname.split('/');
        if (segments.length > 1) return segments.slice(1).join('/');
      }

      return pathname;
    } catch {
      return '';
    }
  };

  const parseFilenameFromContentDisposition = (contentDisposition = '') => {
    const utf8Match = String(contentDisposition).match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]).replace(/(^"|"$)/g, '');
      } catch {
        return utf8Match[1].replace(/(^"|"$)/g, '');
      }
    }

    const simpleMatch = String(contentDisposition).match(/filename=([^;]+)/i);
    return simpleMatch?.[1] ? simpleMatch[1].trim().replace(/(^"|"$)/g, '') : '';
  };

  const handleDocumentDownload = async (doc) => {
    if (!doc || !doc.companyPdf) return;

    const pdfData = doc.companyPdf;
    const url = typeof pdfData === 'object' && pdfData !== null ? pdfData.url : pdfData;
    const filename = getDocumentDownloadFilename(doc);
    const publicId =
      doc.companyPdfPublicId ||
      (typeof pdfData === 'object' && pdfData !== null
        ? (pdfData.public_id || pdfData.publicId || extractObjectKeyFromUrl(pdfData.url || ''))
        : extractObjectKeyFromUrl(String(pdfData || '')));

    try {
      // Use toast to show processing
      const downloadToast = toast.loading('Preparing download...');

      if (!publicId) {
        toast.dismiss(downloadToast);
        throw new Error('Document key not found');
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_BASE_URL}/upload/cloudinary/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(filename)}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!response.ok) throw new Error("Failed to fetch document");

      const blob = await response.blob();
      const headerFilename = parseFilenameFromContentDisposition(
        response.headers.get('content-disposition') || '',
      );
      const finalFilename = headerFilename || filename;
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", finalFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(downloadToast);
      toast.success('Document downloaded successfully');
    } catch (error) {

      toast.error(error.message || 'Failed to download document');
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(index);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(null);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(null);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const mockEvent = { target: { files: e.dataTransfer.files } };
      handlePdfUpload(mockEvent, index);
    }
  };

  const isUploadingPdf = uploadPdfMutation.isPending;

  const handlePdfDelete = (docIndex) => {
    const doc = formData.companyDocuments[docIndex];
    deletePdfMutation.mutate({ publicId: doc?.companyPdfPublicId, docIndex });
  };

  const handleDocumentChange = (index, field, value) => {
    setFormData(prev => {
      const newDocs = [...(prev.companyDocuments || [])];
      if (newDocs[index]) {
        newDocs[index] = { ...newDocs[index], [field]: value };

        // Clear the other method's data to prevent confusion
        if (field === 'documentMethod') {
          if (value === 'upload') {
            newDocs[index].documentLink = '';
          } else {
            newDocs[index].companyPdf = '';
            newDocs[index].companyPdfPublicId = '';
          }
        }
      }
      return { ...prev, companyDocuments: newDocs };
    });
  };

  const addDocument = () => {
    setFormData(prev => ({
      ...prev,
      companyDocuments: [
        ...(prev.companyDocuments || []),
        { documentTypeId: '', documentTypeName: '', documentType: '', documentMethod: 'upload', documentLink: '', companyPdf: '', companyPdfPublicId: '', certificateNumber: '' }
      ]
    }));
  };

  const removeDocument = (index) => {
    const newDocs = [...formData.companyDocuments];
    newDocs.splice(index, 1);
    setFormData(prev => ({ ...prev, companyDocuments: newDocs }));
  };



  const regions = ['North', 'South', 'East', 'West', 'Central', 'North-East', 'North-West', 'South-East', 'South-West'];
  const timelines = ['Immediate (0-30 days)', 'Short-term (1-3 months)', 'Medium-term (3-6 months)', 'Long-term (6+ months)'];

  const sourceOptions = [
    { value: '', label: 'Select Source' }, // Default option
    ...masterSources.map(s => ({ value: s._id || s.id, label: s.name })),
  ];

  // Helper function to format number with Indian currency format (lakhs/crores)
  const formatNumberWithCommas = (value) => {
    if (!value || value === '') return '';
    // Remove all non-digit characters
    const numericValue = String(value).replace(/\D/g, '');
    if (!numericValue) return '';

    // Indian numbering system: last 3 digits, then groups of 2
    // Example: 1234567 → 12,34,567 | 100000 → 1,00,000 | 1000 → 1,000
    const numStr = numericValue;
    const lastThree = numStr.slice(-3);
    const rest = numStr.slice(0, -3);

    if (rest === '') {
      return lastThree;
    }

    // Group remaining digits in pairs of 2 from right to left
    const restReversed = rest.split('').reverse().join('');
    const restGroups = restReversed.match(/.{1,2}/g) || [];
    const restFormatted = restGroups
      .map(chunk => chunk.split('').reverse().join(''))
      .reverse()
      .join(',');

    return restFormatted + ',' + lastThree;
  };

  // Helper function to parse formatted number (remove commas)
  const parseFormattedNumber = (value) => {
    if (!value || value === '') return '';
    // Remove all non-digit characters
    return String(value).replace(/\D/g, '');
  };

  // Removed manual fetch functions as they are replaced by useQuery
  // Removed manual loading states as they are replaced by React Query states

  // Load all countries on mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const formattedCountries = allCountries.map(c => ({
      id: c.isoCode,
      name: c.name,
      isoCode: c.isoCode
    }));
    setCountryOptions(formattedCountries);
  }, []);

  // Update state options when country changes
  useEffect(() => {
    if (selectedLocation.country) {
      const states = State.getStatesOfCountry(selectedLocation.country.isoCode);
      const formattedStates = states.map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode
      }));
      setStateOptions(formattedStates);
    } else {
      setStateOptions([]);
    }
  }, [selectedLocation.country]);

  // Update city options when state changes
  useEffect(() => {
    if (selectedLocation.country && selectedLocation.state) {
      const cities = City.getCitiesOfState(selectedLocation.country.isoCode, selectedLocation.state.isoCode);
      const formattedCities = cities.map(c => ({
        id: c.name,
        name: c.name
      }));
      setCityOptions(formattedCities);
    } else {
      setCityOptions([]);
    }
  }, [selectedLocation.country, selectedLocation.state]);

  const handleCountrySelect = (value) => {
    setSelectedLocation({
      country: value,
      state: null,
      city: null
    });
    setFormData(prev => ({
      ...prev,
      country: value ? value.name : '',
      state: '',
      city: ''
    }));
  };

  const handleStateSelect = (value) => {
    setSelectedLocation(prev => ({
      ...prev,
      state: value,
      city: null
    }));
    setFormData(prev => ({
      ...prev,
      state: value ? value.name : '',
      city: ''
    }));
  };

  const handleCitySelect = (value) => {
    setSelectedLocation(prev => ({
      ...prev,
      city: value
    }));
    setFormData(prev => ({
      ...prev,
      city: value ? value.name : ''
    }));
  };

  // No longer need manual useEffect for initial fetches as useQuery handles it
  // Debounced search for users is still useful to update userSearchTerm
  useEffect(() => {
    if (!isUserDropdownOpen) return;
    const handler = setTimeout(() => {
      // queryClient will refetch as userSearchTerm changes
    }, 400);
    return () => clearTimeout(handler);
  }, [userSearchTerm, isUserDropdownOpen]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
      if (generatorDropdownRef.current && !generatorDropdownRef.current.contains(event.target)) {
        setIsGeneratorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Map backend source to frontend display format (directly populate from backend resolved values)
  const mapSourceToFrontend = (source) => {
    if (!source) return 'Manual Entry';
    if (typeof source === 'object') {
      return source.name || source.label || source.id || 'Manual Entry';
    }
    return source;
  };


  // Map region value to match dropdown options
  const mapRegionToDropdown = (regionValue) => {
    if (!regionValue) return '';
    // If it's already one of the dropdown options, return it
    if (regions.includes(regionValue)) return regionValue;
    // Map state names to regions (if state is provided instead of region)
    const stateToRegionMap = {
      'Jammu and Kashmir': 'North',
      'Himachal Pradesh': 'North',
      'Punjab': 'North',
      'Uttarakhand': 'North',
      'Haryana': 'North',
      'Delhi': 'North',
      'Rajasthan': 'North',
      'Uttar Pradesh': 'North',
      'Karnataka': 'South',
      'Kerala': 'South',
      'Tamil Nadu': 'South',
      'Andhra Pradesh': 'South',
      'Telangana': 'South',
      'Puducherry': 'South',
      'Goa': 'West',
      'Gujarat': 'West',
      'Maharashtra': 'West',
      'Madhya Pradesh': 'Central',
      'Chhattisgarh': 'Central',
      'Bihar': 'East',
      'Jharkhand': 'East',
      'Odisha': 'East',
      'West Bengal': 'East',
      'Assam': 'East',
      'Sikkim': 'East',
      'Arunachal Pradesh': 'East',
      'Manipur': 'East',
      'Meghalaya': 'East',
      'Mizoram': 'East',
      'Nagaland': 'East',
      'Tripura': 'East'
    };
    return stateToRegionMap[regionValue] || '';
  };

  // Map timeline value to match dropdown options
  const mapTimelineToDropdown = (timelineValue) => {
    if (!timelineValue) return '';
    // If it's already one of the dropdown options, return it
    if (timelines.includes(timelineValue)) return timelineValue;
    // Map common timeline formats to dropdown options
    const timelineMap = {
      'immediate': 'Immediate (0-30 days)',
      '0-30 days': 'Immediate (0-30 days)',
      'short-term': 'Short-term (1-3 months)',
      '1-3 months': 'Short-term (1-3 months)',
      'medium-term': 'Medium-term (3-6 months)',
      '3-6 months': 'Medium-term (3-6 months)',
      'long-term': 'Long-term (6+ months)',
      '6+ months': 'Long-term (6+ months)'
    };
    return timelineMap[timelineValue.toLowerCase()] || '';
  };



  // Load lead data when in edit mode
  // Removed manual loadLeadData as it's replaced by useQuery above

  // Required fields
  const getRequiredFields = () => {
    // Only firstName is required; email and phone are optional
    return ['firstName'];
  };

  const validateFormData = () => {
    const newErrors = {};
    const requiredFields = getRequiredFields();

    // Validate required fields
    requiredFields.forEach(field => {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
        newErrors[field] = `${fieldLabel} is required.`;
      }
    });

    // Validate email format
    if (formData.email && !formData.email.includes('@')) {
      newErrors.email = 'Please enter a valid email address.';
    }

    // Validate phone number (must be 10 digits and start with 6-9)
    if (formData.phone) {
      const phoneValidation = validatePhoneInput(formData.phone);
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.error ? (phoneValidation.error.endsWith('.') ? phoneValidation.error : `${phoneValidation.error}.`) : 'Phone number must be 10-15 digits.';
      } else if (!/^\d{10,15}$/.test(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number (10-15 digits).';
      }
    }

    // Validate alternate phone if provided
    if (formData.alternatePhone) {
      const altPhoneValidation = validatePhoneInput(formData.alternatePhone);
      if (!altPhoneValidation.isValid) {
        newErrors.alternatePhone = altPhoneValidation.error ? (altPhoneValidation.error.endsWith('.') ? altPhoneValidation.error : `${altPhoneValidation.error}.`) : 'Phone number must be 10-15 digits.';
      } else if (!/^\d{10,15}$/.test(formData.alternatePhone)) {
        newErrors.alternatePhone = 'Please enter a valid phone number (10-15 digits).';
      }
    }

    // Validate WhatsApp number if provided
    if (formData.whatsappPhone) {
      const whatsappPhoneValidation = validatePhoneInput(formData.whatsappPhone);
      if (!whatsappPhoneValidation.isValid) {
        newErrors.whatsappPhone = whatsappPhoneValidation.error ? (whatsappPhoneValidation.error.endsWith('.') ? whatsappPhoneValidation.error : `${whatsappPhoneValidation.error}.`) : 'Phone number must be 10-15 digits.';
      } else if (!/^\d{10,15}$/.test(formData.whatsappPhone)) {
        newErrors.whatsappPhone = 'Please enter a valid phone number (10-15 digits).';
      }
    }

    // Validate other numeric fields
    const numericFields = ['annualRevenue', 'leadValue', 'budget', 'revenueMonthly', 'revenueOneTime', 'costOneTime', 'costMrr', 'costDurationMonths', 'totalCost', 'probability', 'followupCount'];
    numericFields.forEach(field => {
      const value = formData[field];
      if (value && value !== '') {
        const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
        const minValue = NUMBER_LIMITS[field]?.min || 0;
        const numValidation = validateNumberInput(value, maxValue, minValue);
        if (!numValidation.isValid) {
          newErrors[field] = numValidation.error ? (numValidation.error.endsWith('.') ? numValidation.error : `${numValidation.error}.`) : 'Please enter a valid number.';
        }
      }
    });

    // Validate postal code (optional field)
    if (formData.pincode && formData.pincode.trim() !== '') {
      if (formData.pincode.length > 15) {
        newErrors.pincode = 'Postal Code cannot exceed 15 characters.';
      } else if (!VALID_TEXT_REGEX.test(formData.pincode.trim())) {
        newErrors.pincode = 'Postal Code contains invalid characters.';
      }
    }

    // Validate lastName if provided (optional field)
    if (formData.lastName && formData.lastName.trim() !== '') {
      if (!VALID_TEXT_REGEX.test(formData.lastName.trim())) {
        newErrors.lastName = 'Last name contains invalid characters.';
      }
    }

    // Validate documents
    if (formData.companyDocuments && formData.companyDocuments.length > 0) {
      formData.companyDocuments.forEach((doc, idx) => {
        if (doc.certificateNumber && doc.certificateNumber.trim().length > 20) {
          newErrors[`companyDocuments_${idx}_cert`] = 'Certificate Number cannot exceed 20 characters.';
        }
      });
    }

    // Validate Loss Reason if status is Closed Lost
    if (formData.status === 'closed_lost' && (!formData.lossReason || formData.lossReason.trim() === '')) {
      newErrors.lossReason = 'Loss Reason is required when status is Lost.';
    }

    // Validate Next Follow-up Date — must be today or a future date
    if (formData.nextFollowupDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(formData.nextFollowupDate);

      let isUnchanged = false;
      if (isEditMode && fetchedLead?.nextFollowupDate) {
        try {
          const originalDateStr = new Date(fetchedLead.nextFollowupDate).toISOString().split('T')[0];
          const currentDateStr = new Date(formData.nextFollowupDate).toISOString().split('T')[0];
          if (originalDateStr === currentDateStr) {
            isUnchanged = true;
          }
        } catch (e) {
          // Ignore date parsing errors
        }
      }

      if (selected < today && !isUnchanged) {
        newErrors.nextFollowupDate = 'Next Follow-up Date must be today or a future date.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handler for formatted number inputs (with comma separators)
  const handleFormattedNumberChange = (field, value) => {
    // Remove all non-digit characters
    const numericValue = parseFormattedNumber(value);

    // Validate the numeric value
    const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
    const minValue = NUMBER_LIMITS[field]?.min || 0;

    let processedValue = numericValue;
    let error = '';

    if (numericValue === '') {
      // Allow empty value
      processedValue = '';
    } else {
      const numValue = parseFloat(numericValue);
      if (isNaN(numValue)) {
        processedValue = '';
        error = 'Please enter a valid number.';
      } else if (numValue < minValue) {
        processedValue = String(minValue);
        error = `Value must be at least ${minValue}.`;
      } else if (numValue > maxValue) {
        processedValue = String(maxValue);
        error = `Value cannot exceed ₹${formatIndianAmount(maxValue)}.`;
      } else {
        processedValue = numericValue;
      }
    }

    // Store the numeric value (without commas) in formData
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Set error if validation failed
    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else if (errors[field]) {
      // Clear error if validation passed
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleInputChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
    let processedValue = value;
    let error = '';

    if (fieldType === 'select') {
      // For dropdowns/select, use value directly without sanitization
      processedValue = value || '';
    } else if (field === 'pincode') {
      // For Postal Code (originally pincode), use centralized regex for consistency
      processedValue = String(value || '').replace(INVALID_CHARS_REGEX, '').slice(0, 15);
      if (processedValue.length > 15) {
        error = 'Postal Code cannot exceed 15 characters.';
      }
    } else if (fieldType === 'text') {
      // Sanitize text input using centralized regex
      processedValue = String(value || '').replace(INVALID_CHARS_REGEX, '');
      // Limit length
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'number') {
      // Validate number input
      const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
      const minValue = NUMBER_LIMITS[field]?.min || 0;
      const numValidation = validateNumberInput(value, maxValue, minValue);
      processedValue = numValidation.value;
      error = numValidation.error ? (numValidation.error.endsWith('.') ? numValidation.error : `${numValidation.error}.`) : '';
    } else if (fieldType === 'phone') {
      // Validate phone input
      const phoneValidation = validatePhoneInput(value);
      processedValue = phoneValidation.value;
      const baseError = phoneValidation.error;

      if (baseError) {
        error = baseError.endsWith('.') ? baseError : `${baseError}.`;
      } else if (processedValue && !/^\d{10,15}$/.test(processedValue)) {
        error = 'Please enter a valid phone number (10-15 digits).';
      }
    } else if (fieldType === 'email') {
      // For email, allow email characters including + (plus sign is valid in emails)
      // Use centralized regex for consistency
      processedValue = String(value || '').replace(INVALID_CHARS_REGEX, '');
      processedValue = processedValue.slice(0, maxLength);

      if (processedValue && !processedValue.includes('@')) {
        error = 'Please enter a valid email address.';
      }
    } else if (fieldType === 'url') {
      // For URL, allow URL characters
      processedValue = String(value || '');
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'textarea' || fieldType === 'description' || fieldType === 'address') {
      // For multi-line text, descriptions, and addresses, use centralized regex
      processedValue = String(value || '').replace(INVALID_CHARS_REGEX, '');
      processedValue = processedValue.slice(0, maxLength);
    }

    // Auto-transform to uppercase and enforce character sets for specific fields (removed gst and drugLicense)
    // No specific uppercase validations currently for non-array fields

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Set error if validation failed
    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else if (errors[field]) {
      // Clear error if validation passed
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleBlur = (field) => {
    const value = formData[field];
    let error = '';

    // Validate required fields
    const requiredFields = getRequiredFields();
    if (requiredFields.includes(field) && (!value || (typeof value === 'string' && value.trim() === ''))) {
      const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
      error = `${fieldLabel} is required.`;
    } else {
      // Field-specific validation
      if (field === 'email' && value && !value.includes('@')) {
        error = 'Please enter a valid email address.';
      } else if ((field === 'phone' || field === 'alternatePhone') && value) {
        const phoneValidation = validatePhoneInput(value);
        if (!phoneValidation.isValid) {
          error = phoneValidation.error ? (phoneValidation.error.endsWith('.') ? phoneValidation.error : `${phoneValidation.error}.`) : 'Phone number must be 10-15 digits.';
        } else if (!/^\d{10,15}$/.test(value)) {
          error = 'Please enter a valid phone number (10-15 digits).';
        }
      } else if (field === 'pincode' && value) {
        if (String(value).length > 15) {
          error = 'Postal Code cannot exceed 15 characters.';
        } else if (!/^[a-zA-Z0-9\s]+$/.test(String(value).trim())) {
          error = 'Postal Code must contain only letters and numbers.';
        }
      } else if (['annualRevenue', 'leadValue', 'budget', 'revenueMonthly', 'revenueOneTime', 'costOneTime', 'costMrr', 'costDurationMonths', 'totalCost'].includes(field) && value) {
        const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
        const minValue = NUMBER_LIMITS[field]?.min || 0;
        const numValidation = validateNumberInput(value, maxValue, minValue);
        if (!numValidation.isValid) {
          error = numValidation.error ? (numValidation.error.endsWith('.') ? numValidation.error : `${numValidation.error}.`) : 'Please enter a valid number.';
        }
      }
    }

    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    }
  };

  const handleTagAdd = (tag) => {
    const currentTags = Array.isArray(formData.tags) ? formData.tags : [];
    const trimmedTag = tag.trim();

    // Validate tag length (max 50 characters)
    if (trimmedTag.length > 50) {
      setErrors(prev => ({
        ...prev,
        tags: 'Each tag must be 50 characters or less.'
      }));
      return;
    }

    // Clear tag error if validation passes
    if (errors.tags) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.tags;
        return newErrors;
      });
    }

    if (trimmedTag && !currentTags.includes(trimmedTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...currentTags, trimmedTag]
      }));
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: Array.isArray(prev.tags) ? prev.tags.filter(tag => tag !== tagToRemove) : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    if (!validateFormData()) {
      const requiredFields = getRequiredFields();
      const missingFields = requiredFields.filter(field =>
        !formData[field] || (typeof formData[field] === 'string' && formData[field].trim() === '')
      );

      if (missingFields.length > 0) {
        // Show individual field error messages instead of combined alert
        const fieldLabels = missingFields.map(f =>
          f.charAt(0).toUpperCase() + f.slice(1).replace(/([A-Z])/g, ' $1').trim()
        );

        // Set focus to first missing field
        const firstMissingField = missingFields[0];
        const fieldElements = document.getElementsByName(firstMissingField);
        if (fieldElements.length > 0) {
          fieldElements[0].focus();
        }

        // Don't show alert, let the form validation errors display naturally
        return;
      } else {
        // Show first validation error by focusing on the field
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField && errors[firstErrorField]) {
          // Focus on the first field with an error
          const fieldElements = document.getElementsByName(firstErrorField);
          if (fieldElements.length > 0) {
            fieldElements[0].focus();
          }
        }
      }
      return;
    }

    // Ensure all form data is properly formatted, preserving arrays for certain fields
    const formattedFormData = Object.keys(formData).reduce((acc, key) => {
      const value = formData[key];

      // Send null for empty email/phone so the backend clears the field on update.
      if ((key === 'email' || key === 'phone') && (!value || String(value).trim() === '')) {
        acc[key] = null;
        return acc;
      }

      // Skip lossReason if status is not closed_lost
      if (key === 'lossReason' && formData.status !== 'closed_lost') {
        return acc;
      }

      // Skip display-only fields
      if (key === 'owner' || key === 'originalGeneratorName') {
        return acc;
      }

      // Preserve arrays for tags field
      if (key === 'tags' && Array.isArray(value)) {
        acc[key] = value;
      } else if (key === 'assignedTo' || key === 'originalGenerator' || key === 'industry' || key === 'decisionMaker' || key === 'source' || key === 'category' || key === 'companysize') {
        // ID fields should be null or undefined if empty to avoid BSON casting errors
        acc[key] = value && value !== '' ? value : null;
      } else if (key === 'companyDocuments') {
        // Clean up companyDocuments: remove docs with no method/content and fix empty IDs
        acc[key] = (value || []).map(doc => {
          const docCopy = { ...doc };
          if (docCopy.documentTypeId === '') delete docCopy.documentTypeId;
          if (docCopy.certificateNumber) docCopy.certificateNumber = docCopy.certificateNumber.trim();
          return docCopy;
        }).filter(doc => {
          const hasType = !!(doc.documentTypeId || doc.documentTypeName || doc.documentType);
          const hasContent = !!((doc.certificateNumber && doc.certificateNumber.trim()) || (doc.documentMethod === 'upload' && doc.companyPdf) || (doc.documentMethod === 'link' && doc.documentLink));
          return hasType || hasContent;
        });
      } else {
        // Handle objects specifically to extract ID if they somehow slipped in
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          acc[key] = value._id || value.id || String(value);
        } else {
          acc[key] = value !== null && value !== undefined ? String(value) : '';
        }
      }
      return acc;
    }, {});

    // Add nested geography data for the new backend structure
    const finalPayload = {
      ...formattedFormData,
      costOneTime: formData.costType === 'recurring' ? formattedFormData.costOneTime : '',
      costMrr: formData.costType === 'recurring' ? formattedFormData.costMrr : '',
      costDurationMonths: formData.costType === 'recurring' ? formattedFormData.costDurationMonths : '',
      costQuarterly: formData.costType === 'recurring' ? String(calculatedCostQuarterly) : '',
      totalCost: formData.costType ? String(calculatedTotalCost) : '',
      countryObj: selectedLocation.country,
      stateObj: selectedLocation.state,
      cityObj: selectedLocation.city,
      postalCode: formData.pincode // Pass as postalCode for consistency with new schema
    };

    // Submit lead mutation
    leadMutation.mutate(finalPayload);
  };

  const handleCancel = () => {
    // if (onBack) {
    //   onBack();
    // } else {
    navigate(-1);
    // }
  };

  // Helper function to check if a tab has errors
  const getTabErrors = (tabKey) => {
    const tabFields = {
      personal: ['firstName', 'lastName', 'email', 'phone', 'whatsappPhone', 'alternatePhone', 'designation', 'linkedinProfileUrl'],
      company: ['company', 'industry', 'website', 'companysize', 'annualRevenue', 'gstNumber'],
      address: ['address', 'city', 'state', 'pincode', 'country'],
      lead: ['source', 'status', 'leadGenerationDate', 'leadBucket', 'businessUnit', 'probability', 'expectedCloseDate', 'category', 'region', 'leadValue', 'budget', 'revenueMonthly', 'revenueOneTime', 'revenueType', 'costType', 'costOneTime', 'costMrr', 'costDurationMonths', 'totalCost', 'timeline', 'decisionMaker', 'scoreLabel', 'productRequirement', 'lossReason'],
      additional: ['description', 'notes', 'tags', 'contactDetailsRaw', 'nextAction', 'lastActiveChannel', 'lastActiveDate', 'lastFollowupDate', 'nextFollowupDate']
    };

    return tabFields[tabKey]?.some(field => errors[field]) || false;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        {isEditMode && (
          <button
            type="button"
            onClick={handleCancel}
            className="mr-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-6 w-6 text-gray-500" />
          </button>
        )}
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <UserPlus className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
              {isEditMode ? 'Edit Lead' : 'Manual Lead Entry'}
            </h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">
              {isEditMode ? 'Update lead information' : 'Create a new lead manually'}
            </p>
          </div>
        </div>
      </div>

      {/* Loading state while fetching lead data */}
      {isLoadingLead && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading lead data...</p>
          </div>
        </div>
      )}

      {/* Form */}
      {!isLoadingLead && (
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* Tabs (SaaS Segmented Pill Container) */}
          <div className="bg-[#F8FAFC] rounded-2xl border border-slate-200/80 p-2 shadow-2xs">
            <div className="bg-slate-100/90 p-1.5 rounded-xl border border-slate-200/80 inline-flex space-x-1.5 max-w-full overflow-x-auto no-scrollbar">
              {[
                { key: 'personal', label: 'Personal Information', icon: User },
                { key: 'company', label: 'Company Information', icon: Building },
                { key: 'address', label: 'Address Information', icon: MapPin },
                { key: 'lead', label: 'Lead Information', icon: Tag },
                { key: 'additional', label: 'Additional Information', icon: FileText }
              ].map((tab) => {
                const IconComponent = tab.icon;
                const hasErrors = getTabErrors(tab.key);
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`py-2 px-4 rounded-lg font-bold text-xs flex items-center space-x-2 whitespace-nowrap transition-all duration-200 font-sans cursor-pointer ${
                      isActive
                        ? 'bg-white text-blue-700 shadow-2xs border border-slate-200/70 scale-[1.01]'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                    }`}
                  >
                    <IconComponent className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                    <span>{tab.label}</span>
                    {hasErrors && (
                      <AlertCircle className="h-4 w-4 text-rose-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-8">
            {activeTab === 'personal' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                  <h3 className="text-base font-bold text-slate-900">Personal Information</h3>
                </div>


                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value, 'text', FIELD_LIMITS.firstName)}
                        onBlur={() => handleBlur('firstName')}
                        className={`${errors.firstName ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                        placeholder="Enter first name"
                        aria-invalid={!!errors.firstName}
                        aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.firstName && (
                          <p id="firstName-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.firstName}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.firstName, FIELD_LIMITS.firstName)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value, 'text', FIELD_LIMITS.lastName)}
                        onBlur={() => handleBlur('lastName')}
                        className={`${errors.lastName ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                        placeholder="Enter last name"
                        aria-invalid={!!errors.lastName}
                        aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.lastName && (
                          <p id="lastName-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.lastName}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.lastName, FIELD_LIMITS.lastName)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value, 'email', 100)}
                        onBlur={() => handleBlur('email')}
                        className={`${errors.email ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                        placeholder="Enter email address"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.email && (
                          <p id="email-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.email}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.email, 100)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value, 'phone')}
                        onBlur={() => handleBlur('phone')}
                        className={`${errors.phone ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                        placeholder="Enter phone number (10-15 digits)"
                        maxLength={15}
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? 'phone-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.phone && (
                          <p id="phone-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.phone}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.phone, FIELD_LIMITS.phone)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        WhatsApp Number
                      </label>
                      <input
                        type="tel"
                        name="whatsappPhone"
                        value={formData.whatsappPhone}
                        onChange={(e) => handleInputChange('whatsappPhone', e.target.value, 'phone')}
                        onBlur={() => handleBlur('whatsappPhone')}
                        className={`${errors.whatsappPhone ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                        placeholder="WhatsApp phone with country code"
                        maxLength={15}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Alternate Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.alternatePhone}
                        onChange={(e) => handleInputChange('alternatePhone', e.target.value, 'phone')}
                        onBlur={() => handleBlur('alternatePhone')}
                        className={`${errors.alternatePhone ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                        placeholder="Enter phone number (optional)"
                        maxLength={15}
                        aria-invalid={!!errors.alternatePhone}
                        aria-describedby={errors.alternatePhone ? 'alternatePhone-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.alternatePhone && (
                          <p id="alternatePhone-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.alternatePhone}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.alternatePhone, FIELD_LIMITS.phone)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Designation
                      </label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={(e) => handleInputChange('designation', e.target.value, 'text', 100)}
                        onBlur={() => handleBlur('designation')}
                        className="input-field"
                        placeholder="Enter designation"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        LinkedIn Profile URL
                      </label>
                      <input
                        type="url"
                        name="linkedinProfileUrl"
                        value={formData.linkedinProfileUrl}
                        onChange={(e) => handleInputChange('linkedinProfileUrl', e.target.value, 'text', 200)}
                        onBlur={() => handleBlur('linkedinProfileUrl')}
                        className="input-field"
                        placeholder="linkedin.com/in/..."
                      />
                    </div>

                    {!isSalesExecutive && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assigned To
                        </label>
                        <div className="relative" ref={userDropdownRef}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsUserDropdownOpen(!isUserDropdownOpen);
                            }}
                            disabled={isLoadingUsers}
                            className={`input-field flex items-center justify-between text-left bg-white ${isLoadingUsers ? 'bg-gray-100 cursor-not-allowed' : ''
                              }`}
                          >
                            <span className="truncate">
                              {formData.assignedTo
                                ? (() => {
                                  const selectedUser = (userData || []).find(u => u.id === formData.assignedTo);
                                  return selectedUser
                                    ? `${selectedUser.name} ${selectedUser.email ? `(${selectedUser.email})` : ''}`
                                    : (formData.owner || 'Unknown User');
                                })()
                                : 'Unassigned'}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {/* Dropdown */}
                          {isUserDropdownOpen && (
                            <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                              <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <input
                                    type="text"
                                    value={userSearchTerm}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                                      setUserSearchTerm(value);
                                    }}
                                    placeholder="Search users..."
                                    maxLength={100}
                                    className="input-field pl-8 text-sm"
                                    autoFocus
                                  />
                                </div>
                              </div>
                              <div className="overflow-y-auto max-h-[240px]">
                                {isLoadingUsers && filteredUsers.length === 0 ? (
                                  <div className="p-3 text-sm text-gray-500 text-center">
                                    Loading users...
                                  </div>
                                ) : filteredUsers.length === 0 ? (
                                  <div className="p-3 text-sm text-gray-500 text-center">
                                    {userSearchTerm ? 'No users found' : 'No users available'}
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        handleInputChange('owner', '', 'select');
                                        handleInputChange('assignedTo', '', 'select');
                                        setIsUserDropdownOpen(false);
                                        setUserSearchTerm('');
                                      }}
                                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 ${!formData.assignedTo ? 'bg-gray-100 font-medium' : 'text-gray-700'
                                        }`}
                                    >
                                      Unassigned
                                    </button>
                                    {filteredUsers.map(user => (
                                      <button
                                        key={user.id}
                                        type="button"
                                        onClick={() => {
                                          handleInputChange('owner', user.name, 'select');
                                          handleInputChange('assignedTo', user.id, 'select');
                                          setIsUserDropdownOpen(false);
                                          setUserSearchTerm('');
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${formData.assignedTo === user.id ? 'bg-gray-100 font-medium' : 'text-gray-700'
                                          }`}
                                      >
                                        <div className="truncate font-medium">{user.name}</div>
                                        {user.email && (
                                          <div className="text-xs text-gray-500 truncate">{user.email}</div>
                                        )}
                                        {user.designation && (
                                          <div className="text-xs text-gray-500 truncate capitalize">{user.designation}</div>
                                        )}
                                      </button>
                                    ))}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {isLoadingUsers && !isUserDropdownOpen && (
                          <p className="mt-1 text-xs text-gray-500">Loading users...</p>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Original Generator
                      </label>
                      <div className="relative" ref={generatorDropdownRef}>
                        <button
                          type="button"
                          onClick={() => {
                            setIsGeneratorDropdownOpen(!isGeneratorDropdownOpen);
                          }}
                          disabled={isLoadingUsers}
                          className={`input-field flex items-center justify-between text-left bg-white ${isLoadingUsers ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                        >
                          <span className="truncate">
                            {formData.originalGenerator
                              ? (() => {
                                const selectedUser = (userData || []).find(u => u.id === formData.originalGenerator);
                                return selectedUser
                                  ? `${selectedUser.name} ${selectedUser.email ? `(${selectedUser.email})` : ''}`
                                  : (formData.originalGeneratorName || 'Unknown User');
                              })()
                              : 'Select original generator'}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${isGeneratorDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isGeneratorDropdownOpen && (
                          <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                            <div className="p-2 border-b border-gray-100">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  value={generatorSearchTerm}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                                    setGeneratorSearchTerm(value);
                                  }}
                                  placeholder="Search employees..."
                                  maxLength={100}
                                  className="input-field pl-8 text-sm"
                                  autoFocus
                                />
                              </div>
                            </div>
                            <div className="overflow-y-auto max-h-[240px]">
                              {isLoadingUsers && filteredGenerators.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500 text-center">
                                  Loading employees...
                                </div>
                              ) : filteredGenerators.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500 text-center">
                                  {generatorSearchTerm ? 'No employees found' : 'No employees available'}
                                </div>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleInputChange('originalGenerator', '', 'select');
                                      handleInputChange('originalGeneratorName', '', 'select');
                                      setIsGeneratorDropdownOpen(false);
                                      setGeneratorSearchTerm('');
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 ${!formData.originalGenerator ? 'bg-gray-100 font-medium' : 'text-gray-700'
                                      }`}
                                  >
                                    None
                                  </button>
                                  {filteredGenerators.map((employee) => (
                                    <button
                                      key={employee.id}
                                      type="button"
                                      onClick={() => {
                                        handleInputChange('originalGenerator', employee.id, 'select');
                                        handleInputChange('originalGeneratorName', employee.name, 'select');
                                        setIsGeneratorDropdownOpen(false);
                                        setGeneratorSearchTerm('');
                                      }}
                                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${formData.originalGenerator === employee.id ? 'bg-gray-100 font-medium' : 'text-gray-700'
                                        }`}
                                    >
                                      <div className="truncate font-medium">{employee.name}</div>
                                      {employee.email && (
                                        <div className="text-xs text-gray-500 truncate">{employee.email}</div>
                                      )}
                                      {employee.role && (
                                        <div className="text-xs text-gray-500 truncate capitalize">{employee.role.replace(/_/g, ' ')}</div>
                                      )}
                                    </button>
                                  ))}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {isLoadingUsers && !isGeneratorDropdownOpen && (
                        <p className="mt-1 text-xs text-gray-500">Loading employees...</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'company' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value, 'text', FIELD_LIMITS.company)}
                        className={`${errors.company ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                        placeholder="Enter company name"
                        aria-invalid={!!errors.company}
                        aria-describedby={errors.company ? 'company-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.company && (
                          <p id="company-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.company}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.company, FIELD_LIMITS.company)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value, 'url', 200)}
                        className={`${errors.website ? 'input-field border-red-500 focus:ring-red-500 bg-red-50' : 'input-field'}`}
                        placeholder="https://www.company.com"
                        aria-invalid={!!errors.website}
                        aria-describedby={errors.website ? 'website-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.website && (
                          <p id="website-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.website}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.website, 200)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <select
                        value={formData.industry}
                        onChange={(e) => handleInputChange('industry', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`${errors.industry ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'
                          } ${isLoadingMasters ? 'bg-gray-50' : ''}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Industries...' : 'Select Industry'}</option>
                        {masterIndustries.map((industry) => (
                          <option key={industry._id} value={industry._id}>{toTitleCase(industry.name)}</option>
                        ))}
                        {/* Show lead's existing industry if it's inactive and not in the main list */}
                        {isEditMode && formData.industry && leadIndustryInfo && !masterIndustries.find(i => i._id === formData.industry) && (
                          <option value={leadIndustryInfo._id}>
                            {toTitleCase(leadIndustryInfo.name)} (Inactive)
                          </option>
                        )}
                      </select>
                      {!isLoadingMasters && masterIndustries.length === 0 && !formData.industry && (
                        <p className="mt-1 text-xs text-amber-600">No active industries found in configuration.</p>
                      )}
                      {errors.industry && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.industry}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size
                      </label>
                      <select
                        value={formData.companysize}
                        onChange={(e) => handleInputChange('companysize', e.target.value, 'select')}
                        className="input-field"
                      >
                        <option value="">Select Company Size</option>
                        {masterCompanySizes.map((size) => (
                          <option key={size._id} value={size._id}>{toTitleCase(size.name)}</option>
                        ))}
                        {/* Show lead's existing company size if it's inactive and not in the main list */}
                        {isEditMode && formData.companysize && leadCompanySizeInfo && !masterCompanySizes.find(s => s._id === formData.companysize) && (
                          <option value={leadCompanySizeInfo._id}>
                            {toTitleCase(leadCompanySizeInfo.name)}
                          </option>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        GST Number
                      </label>
                      <input
                        type="text"
                        value={formData.gstNumber}
                        onChange={(e) => handleInputChange('gstNumber', e.target.value.toUpperCase(), 'text', 15)}
                        className="input-field"
                        placeholder="Enter GST number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Annual Revenue
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(formData.annualRevenue)}
                        onChange={(e) => handleFormattedNumberChange('annualRevenue', e.target.value)}
                        className={`input-field transition-all duration-200 ease-in-out ${errors.annualRevenue ? 'border-red-500 focus:ring-red-500' : ''
                          }`}
                        placeholder="Enter annual revenue in ₹"
                        onBlur={() => handleBlur('annualRevenue')}
                        aria-invalid={!!errors.annualRevenue}
                        aria-describedby={errors.annualRevenue ? 'annualRevenue-error' : undefined}
                      />
                      {errors.annualRevenue && (
                        <p id="annualRevenue-error" className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.annualRevenue}
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2 space-y-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Company Documents
                        </label>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={addDocument}
                          className="bg-gray-50 hover:bg-gray-100"
                          size="sm"
                        >
                          + Add Document
                        </Button>
                      </div>

                      {formData.companyDocuments.length === 0 && (
                        <div className="p-4 border border-dashed border-gray-300 rounded-lg text-center text-gray-500 text-sm">
                          No documents added. Click "+ Add Document" to upload or link a document.
                        </div>
                      )}

                      {formData.companyDocuments.map((doc, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50 relative">
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="absolute top-4 right-4 text-red-500 hover:text-red-700 p-1"
                            title="Remove Document"
                          >
                            <X className="h-5 w-5" />
                          </button>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Document Type
                              </label>
                              <select
                                value={doc.documentTypeId || doc.documentType || ''}
                                onChange={(e) => {
                                  const selectedId = e.target.value;
                                  const selectedDocType = masterLeadDocumentTypes.find(dt => dt._id === selectedId);

                                  setFormData(prev => {
                                    const newDocs = [...(prev.companyDocuments || [])];
                                    if (newDocs[index]) {
                                      if (selectedDocType) {
                                        newDocs[index] = {
                                          ...newDocs[index],
                                          documentTypeId: selectedDocType._id,
                                          documentTypeName: selectedDocType.name,
                                          documentType: selectedDocType.name
                                        };
                                      } else {
                                        newDocs[index] = {
                                          ...newDocs[index],
                                          documentTypeId: selectedId,
                                          documentTypeName: selectedId,
                                          documentType: selectedId
                                        };
                                      }
                                    }
                                    return { ...prev, companyDocuments: newDocs };
                                  });
                                }}
                                className="input-field bg-white capitalize"
                              >
                                <option value="" className="normal-case">Select Document Type</option>
                                {masterLeadDocumentTypes.map((dt) => (
                                  <option key={dt._id} value={dt._id}>{toTitleCase(dt.name)}</option>
                                ))}
                                {/* Render existing legacy options only if they don't match our master list */}
                                {doc.documentType && !masterLeadDocumentTypes.find(dt => dt.name === doc.documentType || dt._id === doc.documentTypeId) && (
                                  <option value={doc.documentTypeId || doc.documentType}>
                                    {toTitleCase(doc.documentTypeName || doc.documentType)}
                                  </option>
                                )}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Document/Certificate Number (Optional)
                              </label>
                              <input
                                type="text"
                                value={doc.certificateNumber || ''}
                                onChange={(e) => handleDocumentChange(index, 'certificateNumber', e.target.value.toUpperCase().slice(0, 20))}
                                placeholder="Ex: GSTIN..."
                                maxLength={20}
                                className={`${errors[`companyDocuments_${index}_cert`] ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'} bg-white`}
                              />
                              {errors[`companyDocuments_${index}_cert`] && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                  <AlertCircle className="h-4 w-4 mr-1" />
                                  {errors[`companyDocuments_${index}_cert`]}
                                </p>
                              )}
                            </div>

                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Document Input Method
                              </label>
                              <div className="flex items-center space-x-6 h-[42px]">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`documentMethod_${index}`}
                                    value="upload"
                                    checked={doc.documentMethod === 'upload'}
                                    onChange={(e) => handleDocumentChange(index, 'documentMethod', e.target.value)}
                                    className="w-4 h-4 text-agrosaf-green focus:ring-agrosaf-green"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">Upload Document</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`documentMethod_${index}`}
                                    value="link"
                                    checked={doc.documentMethod === 'link'}
                                    onChange={(e) => handleDocumentChange(index, 'documentMethod', e.target.value)}
                                    className="w-4 h-4 text-agrosaf-green focus:ring-agrosaf-green"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">Document Link</span>
                                </label>
                              </div>
                            </div>
                          </div>

                          {doc.documentMethod === 'link' ? (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Document Link URL
                              </label>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="url"
                                  value={doc.documentLink}
                                  onChange={(e) => handleDocumentChange(index, 'documentLink', e.target.value.slice(0, 500))}
                                  className="input-field bg-white"
                                  placeholder="https://example.com/document.pdf"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!doc.documentLink) {
                                      toast.error('Please enter a link to verify.');
                                      return;
                                    }
                                    try {
                                      // Validate URL format first
                                      new URL(doc.documentLink);
                                      // Open in new tab
                                      window.open(doc.documentLink, '_blank', 'noopener,noreferrer');
                                    } catch (error) {
                                      toast.error('Invalid link format. Please check the URL.');
                                    }
                                  }}
                                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 whitespace-nowrap transition-colors shadow-sm"
                                >
                                  Verify Link
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Upload Document (Max 25MB)
                              </label>
                              <div className="flex-1">
                                {doc.companyPdf ? (
                                  <div className="flex items-center justify-between p-3 border border-agrosaf-green bg-green-50 rounded-lg">
                                    <div className="flex items-center space-x-3 overflow-hidden">
                                      <FileText className="h-5 w-5 text-agrosaf-green flex-shrink-0" />
                                      <span className="text-sm text-gray-700 truncate">
                                        {typeof doc.companyPdf === 'object' && doc.companyPdf !== null
                                          ? doc.companyPdf.name || doc.companyPdf.originalName || 'Document.pdf'
                                          : String(doc.companyPdf || '').split('/').pop() || 'Document.pdf'}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => handleDocumentDownload(doc)}
                                        className="p-1 text-[#0F2942] hover:bg-blue-100 rounded bg-white"
                                        title="Download Document"
                                      >
                                        <LinkIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handlePdfDelete(index)}
                                        className="p-1 text-red-600 hover:bg-red-100 rounded bg-white"
                                        title="Remove Document"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <label
                                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${isUploadingPdf ? 'border-gray-200 bg-gray-50' :
                                      isDragging === index ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                                      }`}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, index)}
                                  >
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                      {isUploadingPdf ? (
                                        <>
                                          <Loader2 className="w-8 h-8 mb-3 text-agrosaf-green animate-spin" />
                                          <p className="text-sm text-gray-500">Uploading Document...</p>
                                        </>
                                      ) : (
                                        <>
                                          <Upload className="w-8 h-8 mb-3 text-gray-400" />
                                          <p className="mb-2 text-sm text-gray-500">
                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                          </p>
                                          <p className="text-xs text-gray-400">PDF only (Max 25MB)</p>
                                        </>
                                      )}
                                    </div>
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept="application/pdf"
                                      onChange={(e) => handlePdfUpload(e, index)}
                                      disabled={isUploadingPdf}
                                    />
                                  </label>
                                )}
                              </div>
                            </div>
                          )}

                        </div>
                      ))}

                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'address' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Address Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <LocationSelect
                        options={countryOptions}
                        value={selectedLocation.country}
                        onChange={handleCountrySelect}
                        onBlur={() => handleBlur('country')}
                        placeholder="Select Country"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <LocationSelect
                        options={stateOptions}
                        value={selectedLocation.state}
                        onChange={handleStateSelect}
                        onBlur={() => handleBlur('state')}
                        placeholder="Select State"
                        disabled={!selectedLocation.country}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <LocationSelect
                        options={cityOptions}
                        value={selectedLocation.city}
                        onChange={handleCitySelect}
                        onBlur={() => handleBlur('city')}
                        placeholder="Select City"
                        disabled={!selectedLocation.state}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value, 'address', FIELD_LIMITS.address)}
                        onBlur={() => handleBlur('address')}
                        className="input-field"
                        placeholder="Street, Building, Floor"
                      />
                      <div className="flex justify-end mt-1">
                        <p className="text-xs text-gray-500">
                          {getCharacterCount(formData.address, FIELD_LIMITS.address)}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Postal Code
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange('pincode', e.target.value)}
                        onBlur={() => handleBlur('pincode')}
                        className={`${errors.pincode ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                        placeholder="Enter postal code"
                        maxLength={15}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.pincode && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.pincode}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.pincode, 15)}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Region
                      </label>
                      <select
                        value={formData.region}
                        onChange={(e) => handleInputChange('region', e.target.value, 'select')}
                        className={`${errors.region ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                      >
                        <option value="">Select Region</option>
                        {['North', 'South', 'East', 'West', 'Central', 'North-East', 'North-West', 'South-East', 'South-West'].map((region) => (
                          <option key={region} value={region}>{region}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'lead' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Lead Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Generation Date
                      </label>
                      <input
                        type="date"
                        value={formData.leadGenerationDate}
                        onChange={(e) => handleInputChange('leadGenerationDate', e.target.value, 'date')}
                        onClick={(e) => e.target.showPicker?.()}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Bucket
                      </label>
                      <select
                        value={formData.leadBucket}
                        onChange={(e) => handleInputChange('leadBucket', e.target.value, 'select')}
                        className="input-field"
                      >
                        <option value="">Select Bucket</option>
                        {masterLeadBuckets.map(bucket => (
                          <option key={bucket._id} value={bucket.name}>{toTitleCase(bucket.name)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Unit
                      </label>
                      <select
                        value={formData.businessUnit}
                        onChange={(e) => handleInputChange('businessUnit', e.target.value, 'select')}
                        className="input-field"
                      >
                        <option value="">Select Business Unit</option>
                        {masterBusinessUnits.map(unit => (
                          <option key={unit._id} value={unit.name}>{toTitleCase(unit.name)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Probability (%)
                      </label>
                      <input
                        type="number"
                        value={formData.probability}
                        onChange={(e) => handleInputChange('probability', e.target.value, 'number')}
                        min="0"
                        max="100"
                        className="input-field"
                        placeholder="Enter probability (0-100)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expected Close Date
                      </label>
                      <input
                        type="date"
                        value={formData.expectedCloseDate}
                        onChange={(e) => handleInputChange('expectedCloseDate', e.target.value, 'date')}
                        onClick={(e) => e.target.showPicker?.()}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Management Intervention
                      </label>
                      <input
                        type="text"
                        value={formData.needManagementIntervention}
                        onChange={(e) => handleInputChange('needManagementIntervention', e.target.value)}
                        className="input-field"
                        placeholder="Assigner name if needed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Staging Instance
                      </label>
                      <input
                        type="url"
                        value={formData.stagingInstance}
                        onChange={(e) => handleInputChange('stagingInstance', e.target.value, 'url')}
                        className="input-field"
                        placeholder="https://demo.domain.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Source
                      </label>
                      <select
                        value={formData.source}
                        onChange={(e) => handleInputChange('source', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`${errors.source ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'
                          } ${isLoadingMasters ? 'bg-gray-50' : ''}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Sources...' : 'Select Source'}</option>
                        {masterSources.map((source) => (
                          <option key={source._id} value={source._id}>{toTitleCase(source.name)}</option>
                        ))}
                        {/* Show lead's existing source if it's inactive and not in the main list */}
                        {isEditMode && formData.source && leadSourceInfo && !masterSources.find(s => s._id === formData.source) && (
                          <option value={leadSourceInfo._id}>
                            {toTitleCase(leadSourceInfo.name)} (Inactive)
                          </option>
                        )}

                      </select>
                      {!isLoadingMasters && masterSources.length === 0 && !formData.source && (
                        <p className="mt-1 text-xs text-amber-600">No active sources found in configuration.</p>
                      )}
                      {errors.source && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.source}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value, 'select')}
                        className={`${errors.status ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                      >
                        {masterLeadStatuses.length > 0 &&
                          masterLeadStatuses.map(s => (
                            <option key={s._id} value={s.value}>{toTitleCase(s.name)}</option>
                          ))
                        }
                      </select>
                      {errors.status && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.status}
                        </p>
                      )}

                    </div>

                    {formData.status === 'closed_lost' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Loss Reason <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          rows={3}
                          value={formData.lossReason}
                          onChange={(e) => handleInputChange('lossReason', e.target.value, 'textarea', 1500)}
                          className={`${errors.lossReason ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                          placeholder="Please explain why this lead was lost..."
                          maxLength={1500}
                        />
                        <div className="flex justify-between items-center mt-1">
                          {errors.lossReason && (
                            <p className="text-sm text-red-600 flex items-center">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {errors.lossReason}
                            </p>
                          )}
                          <span className="text-xs text-gray-500 ml-auto">
                            {getCharacterCount(formData.lossReason, 1500)}
                          </span>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value, 'select')}
                        className={`${errors.category ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'
                          }`}
                        disabled={isLoadingMasters}
                      >
                        <option value="">Select Category</option>
                        {masterLeadCategories.map((cat) => (
                          <option key={cat._id} value={cat._id}>
                            {toTitleCase(cat.name)}
                          </option>
                        ))}
                      </select>
                      {errors.category && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.category}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Region
                      </label>
                      <select
                        value={formData.region}
                        onChange={(e) => handleInputChange('region', e.target.value, 'select')}
                        className={`${errors.region ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'
                          }`}
                      >
                        <option value="">Select Region</option>
                        {regions.map((region) => (
                          <option key={region} value={region}>{region}</option>
                        ))}
                      </select>
                      {errors.region && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.region}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Value
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(formData.leadValue)}
                        onChange={(e) => handleFormattedNumberChange('leadValue', e.target.value)}
                        className={`input-field transition-all duration-200 ease-in-out ${errors.leadValue ? 'border-red-500 focus:ring-red-500' : ''
                          }`}
                        placeholder="Enter lead value in ₹"
                        aria-invalid={!!errors.leadValue}
                        aria-describedby={errors.leadValue ? 'leadValue-error' : undefined}
                      />
                      {errors.leadValue && (
                        <p id="leadValue-error" className="mt-1 text-sm text-red-600 flex items-center transition-opacity duration-200">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.leadValue}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expected Order Value
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(formData.budget)}
                        onChange={(e) => handleFormattedNumberChange('budget', e.target.value)}
                        className={`input-field transition-all duration-200 ease-in-out ${errors.budget ? 'border-red-500 focus:ring-red-500' : ''
                          }`}
                        placeholder="Enter budget in ₹"
                        aria-invalid={!!errors.budget}
                        aria-describedby={errors.budget ? 'budget-error' : undefined}
                      />
                      {errors.budget && (
                        <p id="budget-error" className="mt-1 text-sm text-red-600 flex items-center transition-opacity duration-200">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.budget}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Revenue Monthly
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(formData.revenueMonthly)}
                        onChange={(e) => handleFormattedNumberChange('revenueMonthly', e.target.value)}
                        className={`input-field transition-all duration-200 ease-in-out ${errors.revenueMonthly ? 'border-red-500 focus:ring-red-500' : ''
                          }`}
                        placeholder="Enter monthly revenue in ₹"
                        aria-invalid={!!errors.revenueMonthly}
                        aria-describedby={errors.revenueMonthly ? 'revenueMonthly-error' : undefined}
                      />
                      {errors.revenueMonthly && (
                        <p id="revenueMonthly-error" className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.revenueMonthly}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Revenue One Time
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(formData.revenueOneTime)}
                        onChange={(e) => handleFormattedNumberChange('revenueOneTime', e.target.value)}
                        className={`input-field transition-all duration-200 ease-in-out ${errors.revenueOneTime ? 'border-red-500 focus:ring-red-500' : ''
                          }`}
                        placeholder="Enter one-time revenue in ₹"
                        aria-invalid={!!errors.revenueOneTime}
                        aria-describedby={errors.revenueOneTime ? 'revenueOneTime-error' : undefined}
                      />
                      {errors.revenueOneTime && (
                        <p id="revenueOneTime-error" className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.revenueOneTime}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Revenue Type
                      </label>
                      <select
                        value={formData.revenueType}
                        onChange={(e) => handleInputChange('revenueType', e.target.value, 'select')}
                        className="input-field"
                      >
                        <option value="">Select Revenue Type</option>
                        {masterRevenueTypes.map(type => (
                          <option key={type._id} value={type.name}>{toTitleCase(type.name)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cost Type
                      </label>
                      <select
                        value={formData.costType}
                        onChange={(e) => handleInputChange('costType', e.target.value, 'select')}
                        className="input-field"
                      >
                        <option value="">Select Cost Type</option>
                        <option value="fixed">Fixed</option>
                        <option value="recurring">Recurring</option>
                      </select>
                    </div>

                    {formData.costType === 'recurring' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            One-Time (₹)
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(formData.costOneTime)}
                            onChange={(e) => handleFormattedNumberChange('costOneTime', e.target.value)}
                            className={`input-field transition-all duration-200 ease-in-out ${errors.costOneTime ? 'border-red-500 focus:ring-red-500' : ''
                              }`}
                            placeholder="Enter one-time cost"
                            aria-invalid={!!errors.costOneTime}
                            aria-describedby={errors.costOneTime ? 'costOneTime-error' : undefined}
                          />
                          {errors.costOneTime && (
                            <p id="costOneTime-error" className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {errors.costOneTime}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            MRR (₹)
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(formData.costMrr)}
                            onChange={(e) => handleFormattedNumberChange('costMrr', e.target.value)}
                            className={`input-field transition-all duration-200 ease-in-out ${errors.costMrr ? 'border-red-500 focus:ring-red-500' : ''
                              }`}
                            placeholder="Enter MRR"
                            aria-invalid={!!errors.costMrr}
                            aria-describedby={errors.costMrr ? 'costMrr-error' : undefined}
                          />
                          {errors.costMrr && (
                            <p id="costMrr-error" className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {errors.costMrr}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Duration (Month)
                          </label>
                          <input
                            type="number"
                            value={formData.costDurationMonths}
                            onChange={(e) => handleInputChange('costDurationMonths', e.target.value, 'number')}
                            min="0"
                            className={`input-field ${errors.costDurationMonths ? 'border-red-500 focus:ring-red-500' : ''}`}
                            placeholder="Enter duration"
                            aria-invalid={!!errors.costDurationMonths}
                            aria-describedby={errors.costDurationMonths ? 'costDurationMonths-error' : undefined}
                          />
                          {errors.costDurationMonths && (
                            <p id="costDurationMonths-error" className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {errors.costDurationMonths}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quarterly/Otherwise
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(calculatedCostQuarterly)}
                            readOnly
                            className="input-field bg-gray-50 text-gray-700"
                            aria-readonly="true"
                          />
                          <p className="mt-1 text-xs text-gray-500">Duration x MRR</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Total Cost
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(calculatedTotalCost)}
                            readOnly
                            className="input-field bg-gray-50 text-gray-700"
                            aria-readonly="true"
                          />
                          <p className="mt-1 text-xs text-gray-500">Quarterly/Otherwise + One-Time</p>
                        </div>
                      </>
                    )}

                    {formData.costType === 'fixed' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Total Cost
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(formData.totalCost)}
                            onChange={(e) => handleFormattedNumberChange('totalCost', e.target.value)}
                            className={`input-field transition-all duration-200 ease-in-out ${errors.totalCost ? 'border-red-500 focus:ring-red-500' : ''
                              }`}
                            placeholder="Enter total cost"
                            aria-invalid={!!errors.totalCost}
                            aria-describedby={errors.totalCost ? 'totalCost-error' : undefined}
                          />
                          {errors.totalCost && (
                            <p id="totalCost-error" className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {errors.totalCost}
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timeline
                      </label>
                      <select
                        value={formData.timeline}
                        onChange={(e) => handleInputChange('timeline', e.target.value, 'select')}
                        className="input-field"
                      >
                        <option value="">Select Timeline</option>
                        {timelines.map((timeline) => (
                          <option key={timeline} value={timeline}>{timeline}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Decision Maker
                      </label>
                      <select
                        value={formData.decisionMaker}
                        onChange={(e) => handleInputChange('decisionMaker', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`${isLoadingMasters ? 'input-field bg-gray-50' : 'input-field'}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Decision Makers...' : 'Select Decision Maker'}</option>
                        {masterDecisionMakers.map((dm) => (
                          <option key={dm._id} value={dm._id}>{toTitleCase(dm.name)}</option>
                        ))}
                        {/* Show lead's existing decision maker if it's inactive and not in the main list */}
                        {isEditMode && formData.decisionMaker && leadDecisionMakerInfo && !masterDecisionMakers.find(d => d._id === formData.decisionMaker) && (
                          <option value={leadDecisionMakerInfo._id}>
                            {leadDecisionMakerInfo.name} (Inactive)
                          </option>
                        )}
                      </select>
                      {!isLoadingMasters && masterDecisionMakers.length === 0 && !formData.decisionMaker && (
                        <p className="mt-1 text-xs text-amber-600">No active decision makers found in configuration.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Temperature
                      </label>
                      <div className="flex items-center space-x-2 h-[42px]">
                        {[
                          { value: 'hot', label: 'Hot', icon: '🔥', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', accent: 'bg-red-600' },
                          { value: 'warm', label: 'Warm', icon: '☀️', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', accent: 'bg-orange-600' },
                          { value: 'cold', label: 'Cold', icon: '❄️', color: 'text-[#0B2033]', bg: 'bg-blue-50', border: 'border-blue-200', accent: 'bg-[#0F2942]' }
                        ].map((option) => (
                          <label
                            key={option.value}
                            className={`
                              relative flex items-center px-3 py-1.5 rounded-lg border cursor-pointer transition-all duration-200
                              ${formData.scoreLabel === option.value
                                ? `${option.bg} ${option.border} shadow-sm border-2`
                                : 'bg-white border-gray-200 hover:border-gray-300'
                              }
                            `}
                          >
                            <input
                              type="radio"
                              name="scoreLabel"
                              value={option.value}
                              checked={formData.scoreLabel === option.value}
                              onChange={(e) => handleInputChange('scoreLabel', e.target.value)}
                              className="sr-only"
                            />
                            <span className="mr-1.5 text-sm">{option.icon}</span>
                            <span className={`text-xs font-bold capitalize ${formData.scoreLabel === option.value ? option.color : 'text-gray-500'}`}>
                              {option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product/Requirement
                      </label>
                      <textarea
                        value={formData.productRequirement}
                        onChange={(e) => handleInputChange('productRequirement', e.target.value, 'textarea', 1500)}
                        rows={3}
                        placeholder="Specify product interest or requirement..."
                        className="input-field"
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(formData.productRequirement, 1500)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'additional' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value, 'description', 3000)}
                        rows={4}
                        className="input-field"
                        placeholder="Describe the lead's requirements and interests"
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(formData.description, 3000)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Remarks
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value, 'description', FIELD_LIMITS.notes)}
                        rows={4}
                        className="input-field"
                        placeholder="Add any additional notes about this lead"
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(formData.notes, FIELD_LIMITS.notes)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Details Raw
                      </label>
                      <textarea
                        value={formData.contactDetailsRaw}
                        onChange={(e) => handleInputChange('contactDetailsRaw', e.target.value, 'textarea', 1000)}
                        rows={4}
                        className="input-field"
                        placeholder="Paste raw contact details here"
                        maxLength={1000}
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(formData.contactDetailsRaw, 1000)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Next Action
                      </label>
                      <textarea
                        value={formData.nextAction}
                        onChange={(e) => handleInputChange('nextAction', e.target.value, 'textarea', 1000)}
                        rows={4}
                        className="input-field"
                        placeholder="What is the next step?"
                        maxLength={1000}
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(formData.nextAction, 1000)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Active Channel
                      </label>
                      <select
                        value={formData.lastActiveChannel}
                        onChange={(e) => handleInputChange('lastActiveChannel', e.target.value, 'select')}
                        className="input-field"
                      >
                        <option value="">Select Channel</option>
                        {masterLastActiveChannels.map(channel => (
                          <option key={channel._id} value={channel.name}>{toTitleCase(channel.name)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Active Date
                      </label>
                      <input
                        type="date"
                        value={formData.lastActiveDate}
                        onChange={(e) => handleInputChange('lastActiveDate', e.target.value, 'date')}
                        onClick={(e) => e.target.showPicker?.()}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Followup Date
                      </label>
                      <input
                        type="date"
                        value={formData.lastFollowupDate}
                        onChange={(e) => handleInputChange('lastFollowupDate', e.target.value, 'date')}
                        onClick={(e) => e.target.showPicker?.()}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Next Follow-up Date
                      </label>
                      <input
                        type="date"
                        value={formData.nextFollowupDate}
                        min={
                          isEditMode && fetchedLead?.nextFollowupDate && new Date(fetchedLead.nextFollowupDate) < new Date()
                            ? new Date(fetchedLead.nextFollowupDate).toISOString().split('T')[0]
                            : new Date().toISOString().split('T')[0]
                        }
                        onChange={(e) => handleInputChange('nextFollowupDate', e.target.value, 'date')}
                        onClick={(e) => e.target.showPicker?.()}
                        className={`${errors.nextFollowupDate ? 'input-field border-red-500 focus:ring-red-500' : 'input-field'}`}
                      />
                      {errors.nextFollowupDate && (
                        <p className="text-sm text-red-600 flex items-center mt-1">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.nextFollowupDate}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Followup Count
                      </label>
                      <input
                        type="number"
                        value={formData.followupCount}
                        onChange={(e) => handleInputChange('followupCount', e.target.value, 'number')}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Follow-up Cadence
                      </label>
                      <textarea
                        value={formData.followupCadence}
                        onChange={(e) => handleInputChange('followupCadence', e.target.value, 'textarea', 1000)}
                        rows={4}
                        className="input-field"
                        placeholder="e.g. Twice in a week"
                        maxLength={1000}
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(formData.followupCadence, 1000)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={leadMutation.isPending}
              className="!rounded-xl border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50 px-5 py-2.5 shadow-2xs transition-all"
            >
              {isEditMode ? 'Back' : 'Cancel'}
            </Button>
            <PermissionGate permission="lead_management" action={isEditMode ? "update" : "create"}>
              <Button
                type="submit"
                variant="custom"
                icon={<Save className="h-4 w-4" />}
                disabled={leadMutation.isPending || loading || isLoadingLead}
                className="!bg-[#0F2942] hover:!bg-[#0a1a2a] text-white shadow-sm transition-all rounded-xl px-5 py-2.5 text-xs font-bold active:scale-95 flex items-center gap-2 cursor-pointer"
              >
                {(leadMutation.isPending || loading)
                  ? (isEditMode ? 'Updating Lead...' : 'Creating Lead...')
                  : (isEditMode ? 'Update Lead' : 'Create Lead')}
              </Button>
            </PermissionGate>
          </div>
        </form>
      )
      }

    </div>
  );
};

export default ManualLeadEntry;
